import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  InputAdornment,
  Button,
  Chip,
} from '@mui/material';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';
import AnimatedAvatar from '../AnimatedAvatar';

interface CreditCardEntryDemoProps {
  className?: string;
}

export default function CreditCardEntryDemo({ className }: CreditCardEntryDemoProps) {
  // Use the same card number from presentation app with missing last few digits
  const baseCardNumber = '3056 9300 0902 00';
  const fullCardNumber = '3056 9300 0902 0004';
  
  const [userInput, setUserInput] = useState('');
  const cardRef = useRef<HTMLDivElement>(null);
  const checkoutCardRef = useRef<HTMLDivElement>(null);

  // Combine base card number with user input
  const currentCardNumber = useMemo(() => {
    const cleanInput = userInput.replace(/\D/g, ''); // Remove non-digits
    return baseCardNumber + cleanInput;
  }, [userInput]);

  // Check if card number is complete
  const isComplete = useMemo(() => {
    return currentCardNumber.replace(/\s/g, '').length === 16;
  }, [currentCardNumber]);

  // Check if card number is correct
  const isCorrect = useMemo(() => {
    return currentCardNumber === fullCardNumber;
  }, [currentCardNumber]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow digits and limit to 2 characters (last 2 digits)
    const cleanValue = value.replace(/\D/g, '').slice(0, 2);
    setUserInput(cleanValue);
  }, []);

  return (
    <Box className={className}>
      <Typography
        variant="h3"
        component="h2"
        sx={{
          textAlign: 'center',
          mb: 3,
          fontWeight: 'bold',
          color: '#333',
          fontSize: { xs: '2rem', md: '2.5rem' },
        }}
      >
        Entering the Digital World
      </Typography>

      <Typography
        variant="h6"
        component="p"
        sx={{
          textAlign: 'center',
          mb: 6,
          color: '#666',
          maxWidth: '600px',
          mx: 'auto',
          lineHeight: 1.6,
        }}
      >
        Complete the credit card number and watch the hashatar update in real-time. 
        The visual pattern changes with each digit, providing instant feedback for data accuracy.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: { xs: '20px', sm: '40px', md: '60px' },
          flexWrap: 'wrap',
          px: { xs: 1, sm: 2 },
        }}
      >
        {/* Credit Card Mockup (from presentation) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            Your Credit Card
          </Typography>

          <Box
            sx={{
              width: { xs: '280px', sm: '320px', md: '340px' },
              height: { xs: '176px', sm: '195px', md: '214px' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px',
              padding: { xs: '16px', sm: '20px', md: '24px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              overflow: 'hidden',
              maxWidth: 'calc(100vw - 40px)', // Ensure it never exceeds viewport
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
              },
            }}
          >
            {/* Background Hashatar Pattern */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                width: '80px',
                height: '80px',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              ref={cardRef}
            >
              <AnimatedAvatar 
                text={fullCardNumber.replace(/\s/g, '')} 
                size={30}
                containerRef={cardRef}
              />
            </Box>

            {/* Card Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                }}
              >
                PREMIUM CARD
              </Typography>
              <Box
                sx={{
                  width: '40px',
                  height: '24px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#333',
                    fontSize: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  VISA
                </Typography>
              </Box>
            </Box>

            {/* Chip */}
            <Box
              sx={{
                width: '32px',
                height: '24px',
                backgroundColor: '#f0c14b',
                borderRadius: '4px',
                background: 'linear-gradient(45deg, #f0c14b, #daa520)',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                zIndex: 2,
              }}
            />

            {/* Card Number */}
            <Typography
              variant="h5"
              sx={{
                color: '#fff',
                fontFamily: 'Monaco, "Courier New", monospace',
                letterSpacing: '2px',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                fontSize: '18px',
                zIndex: 2,
              }}
            >
              {fullCardNumber}
            </Typography>

            {/* Card Holder and Expiry */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', zIndex: 2 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '10px',
                    marginBottom: '2px',
                  }}
                >
                  CARD HOLDER
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  ALEX JOHNSON
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '10px',
                    marginBottom: '2px',
                  }}
                >
                  EXPIRES
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    fontFamily: 'Monaco, "Courier New", monospace',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  12/28
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Online Checkout Mockup */}
        <Paper
          sx={{
            width: { xs: '280px', sm: '380px', md: '450px' },
            padding: { xs: '20px', sm: '30px', md: '40px' },
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: '3px dashed #ff9800', // Dashed border to indicate demo
            maxWidth: 'calc(100vw - 40px)', // Ensure it never exceeds viewport
            position: 'relative',
            backgroundColor: '#fafafa', // Slightly different background
          }}
        >
          {/* Demo Badge */}
          <Chip
            label="DEMO FORM"
            color="warning"
            variant="filled"
            sx={{
              position: 'absolute',
              top: -12,
              right: 20,
              fontWeight: 'bold',
              fontSize: '0.75rem',
              backgroundColor: '#ff9800',
              color: '#fff',
            }}
          />
          
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            Mock Checkout Demo
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: '#666',
              fontStyle: 'italic',
            }}
          >
            (Simulated payment interface for demonstration purposes)
          </Typography>

          {/* Order Summary */}
          <Box sx={{ mb: 4, p: 3, backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px dashed #ccc' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Sample Order Summary
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Demo Product</Typography>
              <Typography variant="body2">$0.00</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Tax (Demo)</Typography>
              <Typography variant="body2">$0.00</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: '1px solid #dee2e6' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total (Demo)</Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>$0.00</Typography>
            </Box>
          </Box>

          {/* Card Number Input with Hashatar */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                mb: 1,
                fontWeight: 'bold',
              }}
            >
              Card Number
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="04"
                value={userInput}
                onChange={handleInputChange}
                helperText="Enter the last 2 digits to complete the card number"
                inputProps={{
                  maxLength: 2,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditCardIcon sx={{ 
                        color: '#666', 
                        mr: 1,
                        display: { xs: 'none', sm: 'block' } // Hide icon on mobile
                      }} />
                      <Typography
                        sx={{
                          fontFamily: 'Monaco, "Courier New", monospace',
                          fontSize: { xs: '10px', sm: '12px', md: '14px' },
                          color: '#666',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {baseCardNumber}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '56px', // Match hashatar height
                    animation: !isComplete ? 'pulse 2s infinite' : 'none',
                    '& input': {
                      fontFamily: 'Monaco, "Courier New", monospace',
                      fontSize: { xs: '10px', sm: '12px', md: '14px' },
                      letterSpacing: '0.5px',
                    },
                    '& fieldset': {
                      borderColor: isComplete ? (isCorrect ? '#28a745' : '#dc3545') : '#007bff',
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: isComplete ? (isCorrect ? '#28a745' : '#dc3545') : '#007bff',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isComplete ? (isCorrect ? '#28a745' : '#dc3545') : '#007bff',
                    },
                  },
                }}
              />
              
              {/* Hashatar Module */}
              <Box
                sx={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: isComplete ? (isCorrect ? '#28a745' : '#dc3545') : '#e0e0e0',
                  transition: 'border-color 0.3s ease',
                  backgroundColor: '#000',
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                }}
                ref={checkoutCardRef}
              >
                <AnimatedAvatar 
                  text={currentCardNumber.replace(/\s/g, '')} 
                  size={24}
                  containerRef={checkoutCardRef}
                />
              </Box>
            </Box>
          </Box>

          {/* Other Form Fields */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 'bold' }}>
              Cardholder Name
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Alex Johnson"
              disabled
              sx={{ '& .MuiInputBase-input': { color: '#666' } }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 'bold' }}>
                Expiry Date
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="12/28"
                disabled
                sx={{ '& .MuiInputBase-input': { color: '#666' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 'bold' }}>
                CVV
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="123"
                disabled
                sx={{ '& .MuiInputBase-input': { color: '#666' } }}
              />
            </Box>
          </Box>

          {/* Status Message */}
          <Box sx={{ textAlign: 'center', minHeight: '24px', mb: 3 }}>
            {isComplete && (
              <Typography
                variant="body2"
                sx={{
                  color: isCorrect ? '#28a745' : '#dc3545',
                  fontWeight: 'bold',
                }}
              >
                {isCorrect ? '✓ Card number verified!' : '✗ Incorrect digits, try again'}
              </Typography>
            )}
          </Box>

          {/* Complete Purchase Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isCorrect}
            sx={{
              backgroundColor: isCorrect ? '#ff9800' : '#6c757d',
              color: '#fff',
              fontWeight: 'bold',
              py: 2,
              border: '2px dashed #fff',
              '&:hover': {
                backgroundColor: isCorrect ? '#f57c00' : '#6c757d',
              },
            }}
          >
            Complete Purchase
          </Button>
        </Paper>
      </Box>

      {/* CSS for animations */}
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(0, 123, 255, 0.4);
            }
            50% {
              box-shadow: 0 0 0 8px rgba(0, 123, 255, 0.1);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(0, 123, 255, 0);
            }
          }
        `}
      </style>
    </Box>
  );
}
